import { useUploadImage } from '@/hooks/images/useUploadImage';
import { Upload, UploadFile } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { UploadImageSource } from '@/infrastructure/domain/images';
import { RcFile, UploadRequestOption } from 'rc-upload/lib/interface';

type Props = {
  source: UploadImageSource;
  fileList: UploadFile[];
  setFileList: React.Dispatch<React.SetStateAction<UploadFile[]>>;
  maxCount?: number;
};

const Uploader = ({ source, fileList, setFileList, maxCount = 4 }: Props) => {
  const uploadImage = useUploadImage();

  const onImageUpload = async (options: UploadRequestOption) => {
    const { file, onSuccess, onError } = options;
    try {
      const formData = new FormData();
      formData.append('file', file as Blob);
      formData.append('source', source);

      const res = await uploadImage?.mutateAsync(formData);

      // Update the file list with the uploaded file info
      const updatedFile: UploadFile = {
        uid: (file as RcFile).uid,
        name: (file as RcFile).name,
        status: 'done',
        url: res?.url,
        response: res,
      };

      if (maxCount === 1) {
        // If maxCount is 1, replace the entire list with the new file
        setFileList([updatedFile]);
      } else {
        // Otherwise update existing file or append to list
        setFileList(prev => {
          const exists = prev.some(f => f.uid === updatedFile.uid);
          if (exists) {
            return prev.map(f => (f.uid === updatedFile.uid ? updatedFile : f));
          }
          return [...prev, updatedFile];
        });
      }

      onSuccess?.(res);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Upload failed';
      onError?.(new Error(errorMessage));
    }
  };

  return (
    <Upload
      listType="picture-card"
      className="w-full"
      maxCount={maxCount}
      accept="image/*"
      fileList={fileList}
      customRequest={onImageUpload}
      onChange={({ fileList }) => setFileList(fileList)}
    >
      {fileList.length < maxCount && (
        <div className="flex flex-col items-center justify-center text-center">
          <UploadOutlined className="text-2xl mb-2" />
          <span>Add {maxCount === 1 ? 'Featured Image' : 'Image'}</span>
        </div>
      )}
    </Upload>
  );
};

export default Uploader;
